import Logo from '@/components/Logo'
import SocialIcons from '@/components/SocialIcons'
import type { RadioSettings, SocialLink } from '@/types/radio'
import {LogIn, Menu, X} from 'lucide-react'
import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'

const NAV = [
  { href: '/', label: 'INÍCIO' },
  { href: '/noticias', label: 'NOTÍCIAS' },
  { href: '/#programacao', label: 'PROGRAMAÇÃO' },
  { href: '/#equipe', label: 'EQUIPE' },
  { href: '/#sobre', label: 'SOBRE' },
  { href: '/#contato', label: 'CONTATO' },
]

interface SiteHeaderProps {
  settings: RadioSettings | null
  social: SocialLink[]
}

function navClass(active: boolean) {
  return `rounded-full px-3 py-2 text-[11px] font-extrabold tracking-wide xl:px-4 xl:text-[12px] ${
    active ? 'bg-[#ffd21f] text-[#0b2ea8]' : 'text-white hover:bg-white/10'
  }`
}

function SiteHeader({ settings, social }: SiteHeaderProps) {
  const [open, setOpen] = useState(false)
  const location = useLocation()
  const loginActive = location.pathname === '/login'

  return (
    <header className="sticky top-0 z-40 border-b border-white/10 bg-[#08207c]/90 backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-2 px-3 py-3 sm:gap-4 sm:px-4 lg:px-6">
        <Link to="/" className="flex min-w-0 items-center gap-3 sm:gap-4">
          <Logo className="h-[44px] sm:h-[64px]" />
          <div className="hidden min-w-0 md:block">
            <p className="text-[11px] font-extrabold tracking-[0.18em] text-[#ffd21f]">
              {settings?.city || 'SÃO JOSÉ DO SERIDÓ/RN'}
            </p>
            <p className="mt-1 font-script text-[16px] leading-none text-white/95 xl:text-[18px]">
              {settings?.slogan || 'A rádio da nossa gente!'}
            </p>
          </div>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {NAV.map((item) => {
            const isNews = item.href === '/noticias'
            const active =
              (item.href === '/' && location.pathname === '/') ||
              (isNews && location.pathname.startsWith('/noticias'))
            const className = navClass(active)
            if (item.href.startsWith('/#')) {
              return (
                <a key={item.label} href={item.href} className={className}>
                  {item.label}
                </a>
              )
            }
            return (
              <Link key={item.label} to={item.href} className={className}>
                {item.label}
              </Link>
            )
          })}
          <Link
            to="/login"
            className={`ml-1 inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-[11px] font-black tracking-wide xl:px-4 xl:text-[12px] ${
              loginActive ? 'bg-[#0a9ec4] text-white' : 'bg-[#18c8f5] text-[#071a63] hover:bg-[#3ad4ff]'
            }`}
          >
            <LogIn size={14} />
            LOGIN
          </Link>
        </nav>

        <div className="flex items-center gap-2">
          <SocialIcons links={social} className="hidden md:flex" />
          <button
            type="button"
            className="grid h-11 w-11 place-items-center rounded-full bg-white/10 lg:hidden"
            onClick={() => setOpen((value) => !value)}
            aria-label="Abrir menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open ? (
        <div className="border-t border-white/10 bg-[#071a63] px-3 py-4 sm:px-4 lg:hidden">
          <div className="flex flex-col gap-2">
            {NAV.map((item) =>
              item.href.startsWith('/#') ? (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={() => setOpen(false)}
                  className="rounded-xl bg-white/5 px-4 py-3 text-sm font-bold"
                >
                  {item.label}
                </a>
              ) : (
                <Link
                  key={item.label}
                  to={item.href}
                  onClick={() => setOpen(false)}
                  className="rounded-xl bg-white/5 px-4 py-3 text-sm font-bold"
                >
                  {item.label}
                </Link>
              ),
            )}
            <Link
              to="/login"
              onClick={() => setOpen(false)}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#18c8f5] px-4 py-3 text-sm font-black text-[#071a63]"
            >
              <LogIn size={16} />
              LOGIN
            </Link>
            <SocialIcons links={social} />
          </div>
        </div>
      ) : null}
    </header>
  )
}

export default SiteHeader
